/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Table, Form, Modal, Badge } from 'react-bootstrap'
import { Card } from 'react-bootstrap-v5'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Select from 'react-select'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { numberFormat } from '../helpers/GlobalHelper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import GetPagePermission from '../components/GetPagePermission'

const API = process.env.REACT_APP_API_URL + 'inventory-integrity/'

interface CheckDefinition {
	key: string
	module: string
	title: string
	description: string
	fixLabel: string | null
	order: number
}

interface Issue {
	check: string
	ref: string
	date: string | null
	document: string
	subject: string
	stored: number | string | null
	expected: number | string | null
	difference: number | null
	message: string
	fixable: boolean
	edit: { field: string; label: string; value: any } | null
	accepted?: boolean
	acceptedNote?: string | null
}

interface ScanResult {
	key: string
	issues: Issue[]
	seconds: number
	error: string | null
}

type IssueStatus = 'open' | 'fixable' | 'input' | 'review' | 'accepted' | 'all'

const PAGE_SIZE = 100

/** problems fixed per request; a missing product rate reprices all its sales, so one at a time */
const FIX_BATCH: Record<string, number> = { default: 20, missing_product_rates: 1, raw_average_rates: 5, finished_goods_average_rates: 5 }

const stickyHeaderStyle: React.CSSProperties = {
	position: 'sticky',
	top: 0,
	zIndex: 1,
	backgroundColor: '#f8f9fa',
	whiteSpace: 'nowrap',
}

const amount = (value: any) => (value === null || value === undefined || value === '' ? '-' : typeof value === 'number' ? numberFormat(value) : String(value))

const errorText = (resp: any) =>
	resp?.errorMessage || (resp?.errors ? validationErrorMsg(resp.errors).join('<br>') : 'Something went wrong')

const post = (path: string, body: any) => fetch(API + path, postRequestOptions(body)).then((resp) => resp.json())

/** what the user can do about an issue */
const issueStatus = (issue: Issue): Exclude<IssueStatus, 'open' | 'all'> =>
	issue.accepted ? 'accepted' : issue.fixable ? 'fixable' : issue.edit ? 'input' : 'review'

const issueKey = (issue: Issue) => issue.check + '|' + issue.ref

const escapeHtml = (text: string) =>
	String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c])

const InventoryDataIntegrity: React.FC = () => {
	const hasPermission = GetPagePermission('/inventory-data-integrity')

	const [projects, setProjects] = useState<any[]>([])
	const [project, setProject] = useState<any>(null)
	const [fromDate, setFromDate] = useState('')
	const [toDate, setToDate] = useState('')
	const [tab, setTab] = useState('scan')

	const [definitions, setDefinitions] = useState<CheckDefinition[]>([])
	const [results, setResults] = useState<Record<string, ScanResult>>({})
	const [scanning, setScanning] = useState<string | null>(null)
	const [activeCheck, setActiveCheck] = useState<string | null>(null)
	const [selected, setSelected] = useState<Record<string, boolean>>({})
	const [search, setSearch] = useState('')
	const [minDifference, setMinDifference] = useState('')
	const [status, setStatus] = useState<IssueStatus>('open')
	const [page, setPage] = useState(1)
	const [busy, setBusy] = useState(false)
	const [report, setReport] = useState<any>(null)
	const [progress, setProgress] = useState<string | null>(null)

	const idProjects = project?.value ? Number(project.value) : null
	const scope = { idProjects, fromDate: fromDate || null, toDate: toDate || null }

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.data || []).map((row: any) => ({ label: row.name, value: row.id }))
				setProjects(options)
				if (options.length) setProject(options[0])
			})
			.catch(() => toast.error('Could not load projects', { position: 'bottom-right' }))
		fetch(API + 'checks', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => (resp.success ? setDefinitions(resp.data) : toast.error(errorText(resp), { position: 'bottom-right' })))
			.catch(() => toast.error('Could not load checks', { position: 'bottom-right' }))
	}, [])

	// Results belong to one project and range; clear them when the scope changes
	useEffect(() => {
		setResults({})
		setSelected({})
	}, [idProjects, fromDate, toDate])

	// Show every problem of a project as soon as it is opened
	useEffect(() => {
		if (idProjects && definitions.length) scan([])
	}, [idProjects, definitions.length])

	useEffect(() => setPage(1), [activeCheck, search, minDifference, status, results])

	const modules = useMemo(() => {
		const grouped: Record<string, CheckDefinition[]> = {}
		definitions.forEach((check) => {
			grouped[check.module] = grouped[check.module] || []
			grouped[check.module].push(check)
		})
		return grouped
	}, [definitions])

	const titleOf = (key: string) => definitions.find((d) => d.key === key)?.title || key

	const allIssues = useMemo(() => {
		const list: Issue[] = []
		definitions.forEach((check) => (results[check.key]?.issues || []).forEach((issue) => list.push({ ...issue, check: check.key })))
		return list
	}, [definitions, results])

	const totals = useMemo(() => {
		const count = { open: 0, fixable: 0, input: 0, review: 0, accepted: 0 }
		allIssues.forEach((issue) => {
			const kind = issueStatus(issue)
			count[kind]++
			if (kind !== 'accepted') count.open++
		})
		return count
	}, [allIssues])

	const scan = (keys: string[]) => {
		if (!idProjects) return Promise.resolve()
		setScanning(keys.length === 1 ? keys[0] : 'all')
		return post('scan', { ...scope, checks: keys })
			.then((resp) => {
				if (!resp.success) {
					Swal.fire({ icon: 'error', title: 'Scan failed', html: errorText(resp) })
					return
				}
				setResults((current) => {
					const next = { ...current }
					resp.data.forEach((result: ScanResult) => (next[result.key] = result))
					return next
				})
				setSelected({})
			})
			.catch(() => toast.error('Scan failed', { position: 'bottom-right' }))
			.finally(() => setScanning(null))
	}

	const rescanAfter = (checkKeys: string[]) => {
		// a fix can change later layers (rates -> documents -> vouchers), so refresh everything that was scanned
		const keys = Object.keys(results).length ? Object.keys(results) : checkKeys
		if (keys.length) scan(keys.length === definitions.length ? [] : keys)
	}

	/** refsByCheck empty = every open issue of the check(s) in the range */
	const runFix = (check: string, refsByCheck: Record<string, string[]>, dryRun: boolean) => {
		if (!idProjects) return
		const selectedCount = Object.values(refsByCheck).reduce((total, refs) => total + refs.length, 0)
		const what = check === 'all' ? (selectedCount ? 'selected problems' : 'every fixable check, in order') : titleOf(check)
		const count = selectedCount ? `${selectedCount} selected problem(s)` : 'all open problems in the selected range'
		const confirm = dryRun
			? Promise.resolve({ isConfirmed: true })
			: Swal.fire({
					title: 'Apply the fix?',
					html: `<b>${escapeHtml(what)}</b><br/>${count} in <b>${escapeHtml(project?.label)}</b>.<br/>Documents, rates and vouchers will be changed. Accepted problems are left as they are. Preview it first if you have not.`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Apply',
					customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-secondary' },
					buttonsStyling: false,
			  })
		confirm.then((result: any) => {
			if (!result.isConfirmed) return
			setBusy(true)
			if (dryRun) {
				const body: any = { ...scope, check, dryRun }
				if (check === 'all') body.refsByCheck = refsByCheck
				else body.refs = refsByCheck[check] || []
				post('fix', body)
					.then((resp) => setReport({ ...resp, title: what }))
					.catch(() => toast.error('Preview failed', { position: 'bottom-right' }))
					.finally(() => setBusy(false))
				return
			}

			// A real fix runs check by check (lower layers first) in small batches: each batch is its own short
			// transaction, so a long history neither times out nor holds locks on the database for minutes.
			const keys = check === 'all'
				? definitions
						.filter((d) => d.fixLabel && (!selectedCount || (refsByCheck[d.key] || []).length > 0))
						.sort((a, b) => a.order - b.order)
						.map((d) => d.key)
				: [check]
			const rows: any[] = []
			const started = Date.now()
			const runCheck = (key: string) =>
				((refsByCheck[key] || []).length ? Promise.resolve(refsByCheck[key]) : openFixableRefs(key)).then((refs) => {
					const size = FIX_BATCH[key] || FIX_BATCH.default
					const row = { key, title: titleOf(key), before: refs.length, fixed: 0, remaining: 0, messages: [] as string[] }
					rows.push(row)
					const batches: string[][] = []
					for (let i = 0; i < refs.length; i += size) batches.push(refs.slice(i, i + size))
					return batches.reduce(
						(chain: Promise<void>, batch, index) =>
							chain.then(() => {
								setProgress(`${row.title}: ${index * size} / ${refs.length} done`)
								return post('fix', { ...scope, check: key, refs: batch, dryRun: false }).then((resp) => {
									if (!resp.success) throw new Error(`${row.title}: ${errorText(resp)}`)
									const outcome = resp.result?.checks?.[0] || {}
									row.fixed += outcome.fixed || 0
									row.remaining += outcome.remaining || 0
									;(outcome.messages || []).forEach((m: string) => row.messages.includes(m) || row.messages.push(m))
								})
							}),
						Promise.resolve()
					)
				})
			keys
				.reduce((chain: Promise<void>, key) => chain.then(() => runCheck(key)), Promise.resolve())
				.then(() => setReport({ success: true, dryRun: false, seconds: Math.round((Date.now() - started) / 100) / 10, title: what, result: { checks: rows } }))
				.catch((error) => setReport({ success: false, title: what, errorMessage: `${error.message || 'Fix failed'} Batches before this one were saved.`, result: { checks: rows } }))
				.finally(() => {
					setBusy(false)
					setProgress(null)
					rescanAfter(keys)
				})
		})
	}

	/** rescan one check and return its open fixable refs */
	const openFixableRefs = (key: string): Promise<string[]> =>
		post('scan', { ...scope, checks: [key] }).then((resp) => {
			if (!resp.success) throw new Error(errorText(resp))
			const result: ScanResult = resp.data[0]
			if (result?.error) throw new Error(`${titleOf(key)}: ${result.error}`)
			setResults((current) => ({ ...current, [key]: result }))
			return (result?.issues || []).filter((issue) => issue.fixable && !issue.accepted).map((issue) => issue.ref)
		})

	const editIssue = (issue: Issue) => {
		if (!issue.edit) return
		Swal.fire({
			title: `${issue.edit.label}`,
			html: `${escapeHtml(issue.document)}${issue.subject ? '<br/>' + escapeHtml(issue.subject) : ''}<br/><small>${escapeHtml(issue.message)}</small>`,
			input: 'number',
			inputValue: issue.edit.value ?? '',
			inputAttributes: { step: 'any', min: '0' },
			showCancelButton: true,
			confirmButtonText: 'Save and re-cost',
			customClass: { confirmButton: 'btn btn-primary me-2', cancelButton: 'btn btn-secondary' },
			buttonsStyling: false,
			showLoaderOnConfirm: true,
			preConfirm: (value: any) =>
				post('edit', { ...scope, check: issue.check, ref: issue.ref, value, dryRun: false }).then((resp) => {
					if (!resp.success) Swal.showValidationMessage(errorText(resp))
					return resp
				}),
			allowOutsideClick: () => !Swal.isLoading(),
		}).then((result: any) => {
			if (result.isConfirmed && result.value?.success) {
				toast.success((result.value.result?.messages || ['Saved']).join(' '), { position: 'bottom-right' })
				rescanAfter([issue.check])
			}
		})
	}

	/** accept = mark checked and correct; otherwise open the problems again */
	const acceptIssues = (issues: Issue[], accept: boolean) => {
		if (!issues.length) return
		const byCheck: Record<string, string[]> = {}
		issues.forEach((issue) => (byCheck[issue.check] = [...(byCheck[issue.check] || []), issue.ref]))
		const ask = accept
			? Swal.fire({
					title: `Accept ${issues.length} problem(s) as correct?`,
					html: 'They are hidden from the open list and skipped by fixes while their stored value stays the same.',
					input: 'text',
					inputPlaceholder: 'Note (optional), e.g. checked with store',
					showCancelButton: true,
					confirmButtonText: 'Accept',
					customClass: { confirmButton: 'btn btn-success me-2', cancelButton: 'btn btn-secondary' },
					buttonsStyling: false,
			  })
			: Promise.resolve({ isConfirmed: true, value: null })
		ask.then((result: any) => {
			if (!result.isConfirmed) return
			setBusy(true)
			Object.entries(byCheck)
				.reduce(
					(chain: Promise<string[]>, [check, refs]) =>
						chain.then((messages) =>
							post('accept', { ...scope, check, refs, accept, note: result.value || null }).then((resp) => {
								if (!resp.success) throw new Error(errorText(resp))
								return [...messages, ...(resp.result?.messages || [])]
							})
						),
					Promise.resolve([] as string[])
				)
				.then((messages) => {
					toast.success(messages.join(' ') || 'Saved', { position: 'bottom-right' })
					return scan(Object.keys(byCheck).length === definitions.length ? [] : Object.keys(byCheck))
				})
				.catch((error) => Swal.fire({ icon: 'error', title: 'Not saved', html: error.message }))
				.finally(() => setBusy(false))
		})
	}

	const activeDefinition = definitions.find((d) => d.key === activeCheck) || null
	const visibleIssues = useMemo(() => {
		const issues = activeCheck ? allIssues.filter((issue) => issue.check === activeCheck) : allIssues
		const term = search.trim().toLowerCase()
		const min = Number(minDifference) || 0
		return issues.filter((issue) => {
			const kind = issueStatus(issue)
			if (status === 'open' && kind === 'accepted') return false
			if (status !== 'open' && status !== 'all' && kind !== status) return false
			if (term && ![issue.document, issue.subject, issue.message, issue.ref, titleOf(issue.check)].some((v) => (v || '').toLowerCase().includes(term))) return false
			return !min || issue.difference === null || Math.abs(issue.difference) >= min
		})
	}, [activeCheck, allIssues, search, minDifference, status])
	const lastPage = Math.max(1, Math.ceil(visibleIssues.length / PAGE_SIZE))
	const pageIssues = visibleIssues.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
	const selectedIssues = visibleIssues.filter((issue) => selected[issueKey(issue)])
	const selectedFixable = selectedIssues.filter((issue) => issue.fixable && !issue.accepted)
	const selectedOpen = selectedIssues.filter((issue) => !issue.accepted)
	const selectedAccepted = selectedIssues.filter((issue) => issue.accepted)
	const refsByCheck = (issues: Issue[]) => {
		const map: Record<string, string[]> = {}
		issues.forEach((issue) => (map[issue.check] = [...(map[issue.check] || []), issue.ref]))
		return map
	}
	const scanned = Object.keys(results).length > 0

	if (hasPermission !== 1) {
		return null
	}

	const summaryCard = (label: string, value: number, variant: string, filter: IssueStatus, hint: string) => (
		<div className='col-6 col-md' key={label}>
			<div
				role='button'
				className={`border rounded p-3 h-100 ${status === filter ? 'border-' + variant + ' bg-light' : ''}`}
				onClick={() => { setStatus(filter); setActiveCheck(null) }}
				title={hint}
			>
				<div className={`fs-2 fw-bold text-${variant}`}>{scanned ? value : '-'}</div>
				<div className='fw-semibold'>{label}</div>
				<div className='text-muted small'>{hint}</div>
			</div>
		</div>
	)

	const scanTab = (
		<>
			<div className='row g-3 mb-4'>
				{summaryCard('Open problems', totals.open, totals.open ? 'danger' : 'success', 'open', 'Everything not accepted yet')}
				{summaryCard('Fixable', totals.fixable, 'primary', 'fixable', 'Fixed automatically from the documents')}
				{summaryCard('Need your input', totals.input, 'warning', 'input', 'Enter the right rate with Edit')}
				{summaryCard('Review only', totals.review, 'secondary', 'review', 'Check them, then Accept')}
				{summaryCard('Accepted', totals.accepted, 'success', 'accepted', 'Checked and marked correct')}
			</div>

			<div className='d-flex flex-wrap gap-2 mb-3'>
				<button className='btn btn-sm btn-primary' disabled={!idProjects || !!scanning || busy} onClick={() => scan([])}>
					{scanning === 'all' ? <span className='spinner-border spinner-border-sm me-2'></span> : <i className='fas fa-search me-1'></i>}
					Scan all checks
				</button>
				<button className='btn btn-sm btn-light-primary' disabled={!idProjects || busy} onClick={() => runFix('all', {}, true)}>
					<i className='fas fa-eye me-1'></i>Preview fix all (in order)
				</button>
				<button className='btn btn-sm btn-danger' disabled={!idProjects || busy} onClick={() => runFix('all', {}, false)}>
					<i className='fas fa-wrench me-1'></i>Fix all (in order)
				</button>
				{(busy || scanning === 'all') && (
					<span className='text-muted small align-self-center'>
						<span className='spinner-border spinner-border-sm me-2'></span>{busy ? progress || 'Working, this can take a few minutes…' : 'Scanning every check…'}
					</span>
				)}
			</div>

			<div style={{ maxHeight: '40vh', overflow: 'auto' }} className='mb-4'>
				<Table bordered hover size='sm' className='mb-0'>
					<thead>
						<tr>
							<th style={stickyHeaderStyle}>Module</th>
							<th style={stickyHeaderStyle}>Check</th>
							<th className='text-end' style={stickyHeaderStyle}>Open</th>
							<th className='text-end' style={stickyHeaderStyle}>Fixable</th>
							<th className='text-end' style={stickyHeaderStyle}>Need input</th>
							<th className='text-end' style={stickyHeaderStyle}>Accepted</th>
							<th className='text-end' style={stickyHeaderStyle}>Time</th>
							<th style={stickyHeaderStyle}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{Object.entries(modules).map(([module, checks]) =>
							checks.map((check, index) => {
								const result = results[check.key]
								const issues = result?.issues || []
								const open = issues.filter((i) => !i.accepted)
								return (
									<tr key={check.key} className={activeCheck === check.key ? 'table-active' : ''}>
										{index === 0 && <td rowSpan={checks.length} className='fw-bold align-middle'>{module}</td>}
										<td>
											<button type='button' className='btn btn-link p-0 text-start align-baseline' onClick={() => setActiveCheck(activeCheck === check.key ? null : check.key)}>{check.title}</button>
											{result?.error && <div className='text-danger small'>{result.error}</div>}
										</td>
										<td className='text-end'>
											{result ? <Badge bg={open.length ? 'warning' : 'success'} text='dark'>{open.length}</Badge> : '-'}
										</td>
										<td className='text-end'>{result ? open.filter((i) => i.fixable).length : '-'}</td>
										<td className='text-end'>{result ? open.filter((i) => !i.fixable && i.edit).length : '-'}</td>
										<td className='text-end text-muted'>{result ? issues.length - open.length : '-'}</td>
										<td className='text-end text-muted small'>{result ? result.seconds + 's' : ''}</td>
										<td className='text-nowrap'>
											<button className='btn btn-sm btn-light p-1 px-2 me-1' disabled={!idProjects || !!scanning || busy} onClick={() => { setActiveCheck(check.key); scan([check.key]) }}>
												{scanning === check.key ? <span className='spinner-border spinner-border-sm'></span> : 'Scan'}
											</button>
											{check.fixLabel && (
												<>
													<button className='btn btn-sm btn-light-primary p-1 px-2 me-1' disabled={!idProjects || busy} onClick={() => runFix(check.key, {}, true)}>Preview</button>
													<button className='btn btn-sm btn-light-danger p-1 px-2' disabled={!idProjects || busy} onClick={() => runFix(check.key, {}, false)}>{check.fixLabel}</button>
												</>
											)}
										</td>
									</tr>
								)
							})
						)}
					</tbody>
				</Table>
			</div>

			<Card className='border'>
				<Card.Body>
					<div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
						<Card.Title className='h6 mb-0'>{activeDefinition ? activeDefinition.title : 'All problems'}</Card.Title>
						{activeDefinition && (
							<button className='btn btn-sm btn-link p-0' onClick={() => setActiveCheck(null)}>show all checks</button>
						)}
					</div>
					<p className='text-muted small mb-3'>
						{activeDefinition
							? activeDefinition.description
							: 'Every problem found by every check. Fix the fixable ones, Edit the ones that need a rate from you, and Accept the ones you have checked and are correct.'}
					</p>
					{!scanned ? (
						<div className='text-muted'>{scanning ? 'Scanning…' : 'Not scanned yet for this project and range.'}</div>
					) : (
						<>
							<div className='d-flex flex-wrap gap-2 align-items-center mb-2'>
								<Form.Select size='sm' style={{ maxWidth: 170 }} value={status} onChange={(e) => setStatus(e.target.value as IssueStatus)}>
									<option value='open'>Open problems</option>
									<option value='fixable'>Fixable</option>
									<option value='input'>Need your input</option>
									<option value='review'>Review only</option>
									<option value='accepted'>Accepted</option>
									<option value='all'>Everything</option>
								</Form.Select>
								<Form.Control size='sm' style={{ maxWidth: 240 }} placeholder='Search document, item, message…' value={search} onChange={(e) => setSearch(e.target.value)} />
								<Form.Control size='sm' style={{ maxWidth: 150 }} type='number' placeholder='Min difference' value={minDifference} onChange={(e) => setMinDifference(e.target.value)} />
								<span className='text-muted small'>{visibleIssues.length} shown, {selectedIssues.length} selected</span>
								<div className='ms-auto d-flex flex-wrap gap-2'>
									<button className='btn btn-sm btn-light-primary' disabled={!selectedFixable.length || busy} onClick={() => runFix('all', refsByCheck(selectedFixable), true)}>
										Preview fix ({selectedFixable.length})
									</button>
									<button className='btn btn-sm btn-danger' disabled={!selectedFixable.length || busy} onClick={() => runFix('all', refsByCheck(selectedFixable), false)}>
										Fix selected ({selectedFixable.length})
									</button>
									<button className='btn btn-sm btn-light-success' disabled={!selectedOpen.length || busy} onClick={() => acceptIssues(selectedOpen, true)}>
										Accept ({selectedOpen.length})
									</button>
									{selectedAccepted.length > 0 && (
										<button className='btn btn-sm btn-light-warning' disabled={busy} onClick={() => acceptIssues(selectedAccepted, false)}>
											Open again ({selectedAccepted.length})
										</button>
									)}
								</div>
							</div>
							<div style={{ maxHeight: '55vh', overflow: 'auto' }}>
								<Table bordered hover size='sm' className='mb-0'>
									<thead>
										<tr>
											<th style={stickyHeaderStyle}>
												<Form.Check
													type='checkbox'
													title='Select every problem shown (all pages)'
													checked={visibleIssues.length > 0 && visibleIssues.every((i) => selected[issueKey(i)])}
													onChange={(e) => {
														const next = { ...selected }
														visibleIssues.forEach((i) => (next[issueKey(i)] = e.target.checked))
														setSelected(next)
													}}
												/>
											</th>
											{!activeDefinition && <th style={stickyHeaderStyle}>Check</th>}
											<th style={stickyHeaderStyle}>Date</th>
											<th style={stickyHeaderStyle}>Document</th>
											<th style={stickyHeaderStyle}>Item / Product</th>
											<th className='text-end' style={stickyHeaderStyle}>Stored</th>
											<th className='text-end' style={stickyHeaderStyle}>Expected</th>
											<th className='text-end' style={stickyHeaderStyle}>Difference</th>
											<th style={stickyHeaderStyle}>Problem</th>
											<th style={stickyHeaderStyle}>Actions</th>
										</tr>
									</thead>
									<tbody>
										{pageIssues.length === 0 ? (
											<tr><td colSpan={10} className='text-center text-muted py-3'>{totals.open === 0 && status === 'open' ? 'No open problems. Everything is solved.' : 'No problems match.'}</td></tr>
										) : (
											pageIssues.map((issue) => {
												const kind = issueStatus(issue)
												return (
													<tr key={issueKey(issue)} className={issue.accepted ? 'text-muted' : ''}>
														<td>
															<Form.Check type='checkbox' checked={!!selected[issueKey(issue)]} onChange={(e) => setSelected({ ...selected, [issueKey(issue)]: e.target.checked })} />
														</td>
														{!activeDefinition && (
															<td className='small'>
																<button type='button' className='btn btn-link btn-sm p-0 text-start align-baseline' onClick={() => setActiveCheck(issue.check)}>{titleOf(issue.check)}</button>
															</td>
														)}
														<td className='text-nowrap'>{issue.date || '-'}</td>
														<td className='text-nowrap'>{issue.document}</td>
														<td>{issue.subject || '-'}</td>
														<td className='text-end'>{amount(issue.stored)}</td>
														<td className='text-end'>{amount(issue.expected)}</td>
														<td className={`text-end${issue.difference && issue.difference < 0 ? ' text-danger' : ''}`}>{amount(issue.difference)}</td>
														<td className='small'>
															{issue.message}
															{kind === 'input' && <Badge bg='warning' text='dark' className='ms-1'>needs input</Badge>}
															{kind === 'review' && <Badge bg='secondary' className='ms-1'>review</Badge>}
															{kind === 'accepted' && <div><Badge bg='success'>accepted</Badge> <span className='text-muted'>{issue.acceptedNote}</span></div>}
														</td>
														<td className='text-nowrap'>
															{issue.fixable && !issue.accepted && (
																<button className='btn btn-sm btn-light-danger p-1 px-2 me-1' title='Fix this problem' disabled={busy}
																	onClick={() => runFix('all', { [issue.check]: [issue.ref] }, false)}>
																	<i className='fas fa-wrench'></i>
																</button>
															)}
															{issue.edit && !issue.accepted && (
																<button className='btn btn-sm btn-light-warning p-1 px-2 me-1' title={'Edit: ' + issue.edit.label} disabled={busy} onClick={() => editIssue(issue)}>
																	<i className='fas fa-pen'></i>
																</button>
															)}
															{issue.accepted ? (
																<button className='btn btn-sm btn-light p-1 px-2' title='Open again' disabled={busy} onClick={() => acceptIssues([issue], false)}>
																	<i className='fas fa-undo'></i>
																</button>
															) : (
																<button className='btn btn-sm btn-light-success p-1 px-2' title='Accept as correct' disabled={busy} onClick={() => acceptIssues([issue], true)}>
																	<i className='fas fa-check'></i>
																</button>
															)}
														</td>
													</tr>
												)
											})
										)}
									</tbody>
								</Table>
							</div>
							{lastPage > 1 && (
								<div className='d-flex justify-content-end align-items-center mt-2'>
									<button className='btn btn-sm btn-light me-1' disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
									<span className='small mx-2'>{page} / {lastPage}</span>
									<button className='btn btn-sm btn-light' disabled={page >= lastPage} onClick={() => setPage(page + 1)}>Next</button>
								</div>
							)}
						</>
					)}
				</Card.Body>
			</Card>
		</>
	)

	return (
		<Card className='mb-4'>
			<Card.Body>
				<Card.Title className='h5 mb-1'>Inventory Data Integrity</Card.Title>
				<p className='text-muted small mb-3'>
					Finds stock rates, document costs and accounting vouchers that do not match the average rate (or fixed rate) setting of the project,
					and solves them. Every fix can be previewed first: a preview runs the fix and rolls it back. Every change is kept in Run History.
				</p>
				<div className='row align-items-end'>
					<div className='col-md-4'>
						<Form.Group className='mb-3' controlId='project'>
							<Form.Label>Project<span className='required'></span></Form.Label>
							<Select
								value={project}
								onChange={(selectedProject: any) => setProject(selectedProject)}
								options={projects}
								menuPosition='fixed'
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								placeholder='Select Project'
							/>
						</Form.Group>
					</div>
					<div className='col-md-2'>
						<Form.Group className='mb-3' controlId='fromDate'>
							<Form.Label>From Date</Form.Label>
							<Form.Control type='date' value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-2'>
						<Form.Group className='mb-3' controlId='toDate'>
							<Form.Label>To Date</Form.Label>
							<Form.Control type='date' value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
						</Form.Group>
					</div>
					<div className='col-md-4 mb-3 text-muted small'>
						Leave the dates empty to cover the whole history. Fixes only touch documents dated within the range.
					</div>
				</div>

				<Tabs activeKey={tab} onSelect={(key) => setTab(key || 'scan')} id='inventory-integrity-tabs' className='mb-3'>
					<Tab eventKey='scan' title={<>Problems {scanned && <Badge bg={totals.open ? 'danger' : 'success'} className='ms-1'>{totals.open}</Badge>}</>}>
						{tab === 'scan' && scanTab}
					</Tab>
					<Tab eventKey='rates' title='Average Rates'>
						{tab === 'rates' && idProjects && <AverageRatesTab scope={scope} projectLabel={project?.label} />}
					</Tab>
					<Tab eventKey='valuation' title='Inventory vs Accounting'>
						{tab === 'valuation' && idProjects && <ValuationTab scope={scope} />}
					</Tab>
					<Tab eventKey='migrations' title='Data Migrations'>
						{tab === 'migrations' && <DataMigrationsTab />}
					</Tab>
					<Tab eventKey='runs' title='Run History'>
						{tab === 'runs' && idProjects && <RunHistoryTab idProjects={idProjects} />}
					</Tab>
				</Tabs>
			</Card.Body>

			<Modal show={!!report} onHide={() => setReport(null)} size='lg'>
				<Modal.Header closeButton>
					<Modal.Title>{report?.dryRun ? 'Preview' : 'Fix result'}: {report?.title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{report && !report.success && (
						<div className='alert alert-danger'>{report.result?.checks?.length ? '' : 'Nothing was changed: '}{report.errorMessage || report.error}</div>
					)}
					{(report?.success || report?.result?.checks?.length > 0) && (
						<>
							{report.success && (
								<p className={report.dryRun ? 'text-primary' : 'text-success'}>
									{report.dryRun ? 'Preview only: the changes below were rolled back.' : 'Changes saved.'} ({report.seconds}s)
								</p>
							)}
							<Table bordered size='sm'>
								<thead>
									<tr><th>Check</th><th className='text-end'>Problems before</th><th className='text-end'>Fixed</th><th className='text-end'>Still open</th></tr>
								</thead>
								<tbody>
									{(report.result?.checks || []).map((row: any) => (
										<tr key={row.key}>
											<td>{row.title}{(row.messages || []).length > 0 && <div className='text-muted small'>{row.messages.join(' ')}</div>}</td>
											<td className='text-end'>{row.before}</td>
											<td className='text-end'>{row.fixed}</td>
											<td className='text-end'>{row.remaining}</td>
										</tr>
									))}
								</tbody>
							</Table>
							{(report.result?.checks || []).some((row: any) => row.remaining > 0) && (
								<p className='text-muted small mb-0'>Problems still open need a rate from you (Edit) or a review (Accept when correct).</p>
							)}
						</>
					)}
				</Modal.Body>
			</Modal>
		</Card>
	)
}

const AverageRatesTab: React.FC<{ scope: any; projectLabel: string }> = ({ scope, projectLabel }) => {
	const [kind, setKind] = useState<'item' | 'product'>('item')
	const [type, setType] = useState('')
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [data, setData] = useState<any>({ rows: [], total: 0, perPage: 50 })
	const [loading, setLoading] = useState(false)
	const [editing, setEditing] = useState<any>(null)
	const [cascade, setCascade] = useState(false)

	const load = () => {
		setLoading(true)
		post('rates', { ...scope, kind, type, search, page, perPage: 50 })
			.then((resp) => (resp.success ? setData(resp.data) : toast.error(errorText(resp), { position: 'bottom-right' })))
			.finally(() => setLoading(false))
	}
	useEffect(load, [scope.idProjects, scope.fromDate, scope.toDate, kind, type, page])

	const afterChange = (resp: any) => {
		if (resp.success) {
			toast.success((resp.result?.messages || ['Saved']).join(' '), { position: 'bottom-right' })
			setEditing(null)
			load()
		} else {
			Swal.fire({ icon: 'error', title: 'Nothing was changed', html: errorText(resp) })
		}
	}

	const save = () =>
		post('rates/save', {
			...scope,
			kind,
			id: editing.id || null,
			subjectId: editing.subjectId,
			effectiveDate: editing.effectiveDate,
			rate: editing.rate,
			note: editing.note || null,
			cascade,
		}).then(afterChange)

	const remove = (row: any) =>
		Swal.fire({
			title: 'Delete this rate?',
			html: `${row.subjectName}<br/>${row.effectiveDate} @ ${numberFormat(row.rate)}<br/>The history is rebuilt from ${row.effectiveDate}${cascade ? ' and documents are re-costed' : ''}.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Delete',
			customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-secondary' },
			buttonsStyling: false,
		}).then((result: any) => result.isConfirmed && post('rates/delete', { ...scope, kind, id: row.id, cascade }).then(afterChange))

	const rebuild = (dryRun: boolean) =>
		(dryRun
			? Promise.resolve({ isConfirmed: true })
			: Swal.fire({
					title: 'Rebuild all average rates?',
					html: `${kind === 'item' ? 'Raw materials' : 'Finished goods'} of <b>${projectLabel}</b> from ${scope.fromDate || 'the beginning'}${cascade ? ', re-costing documents and vouchers' : ''}.`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Rebuild',
					customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-secondary' },
					buttonsStyling: false,
			  })
		).then((result: any) => {
			if (!result.isConfirmed) return
			setLoading(true)
			post('rates/rebuild', { ...scope, kind, cascade, dryRun })
				.then((resp) => {
					if (resp.success) {
						Swal.fire({ icon: 'info', title: dryRun ? 'Preview (rolled back)' : 'Rebuilt', html: (resp.result?.messages || []).join('<br/>') })
						if (!dryRun) load()
					} else {
						Swal.fire({ icon: 'error', title: 'Nothing was changed', html: errorText(resp) })
					}
				})
				.finally(() => setLoading(false))
		})

	const lastPage = Math.max(1, Math.ceil((data.total || 0) / (data.perPage || 50)))

	return (
		<>
			<p className='text-muted small'>
				Rows from purchases, opening stock, adjustments, production and packing are rebuilt from the stock ledger. A hand-set rate fixes the
				average at the end of its date (like the starting rates); add one to correct the average, then rebuild.
			</p>
			<div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
				<Form.Select size='sm' style={{ maxWidth: 170 }} value={kind} onChange={(e) => { setKind(e.target.value as any); setPage(1) }}>
					<option value='item'>Raw materials</option>
					<option value='product'>Finished goods</option>
				</Form.Select>
				<Form.Select size='sm' style={{ maxWidth: 170 }} value={type} onChange={(e) => { setType(e.target.value); setPage(1) }}>
					<option value=''>All rows</option>
					<option value='anchor'>Hand-set rates</option>
					<option value='ledger'>From documents</option>
				</Form.Select>
				<Form.Control size='sm' style={{ maxWidth: 220 }} placeholder='Search name, press Enter' value={search}
					onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load() } }} />
				<Form.Check type='checkbox' id='rates-cascade' label='Also re-cost documents and vouchers' checked={cascade} onChange={(e) => setCascade(e.target.checked)} />
				<button className='btn btn-sm btn-light-primary ms-auto' disabled={loading} onClick={() => rebuild(true)}>Preview rebuild</button>
				<button className='btn btn-sm btn-danger' disabled={loading} onClick={() => rebuild(false)}>Rebuild all</button>
			</div>
			<div style={{ maxHeight: '55vh', overflow: 'auto' }}>
				<Table bordered hover size='sm' className='mb-0'>
					<thead>
						<tr>
							<th style={stickyHeaderStyle}>Date</th>
							<th style={stickyHeaderStyle}>{kind === 'item' ? 'Item' : 'Product'}</th>
							<th className='text-end' style={stickyHeaderStyle}>Rate</th>
							<th style={stickyHeaderStyle}>Source</th>
							<th style={stickyHeaderStyle}>Created</th>
							<th style={stickyHeaderStyle}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr><td colSpan={6} className='text-center py-3'><span className='spinner-border spinner-border-sm me-2'></span>Loading…</td></tr>
						) : data.rows.length === 0 ? (
							<tr><td colSpan={6} className='text-center text-muted py-3'>No rows.</td></tr>
						) : (
							data.rows.map((row: any) => (
								<tr key={row.id}>
									<td className='text-nowrap'>{row.effectiveDate}</td>
									<td>{row.subjectName}</td>
									<td className='text-end'>{numberFormat(Number(row.rate))}</td>
									<td>{row.anchor ? <Badge bg='info'>hand-set</Badge> : row.refType} {row.refId ? '#' + row.refId : ''}</td>
									<td className='small text-muted text-nowrap'>{row.createdBy} {row.createdAt}</td>
									<td className='text-nowrap'>
										<button className='btn btn-sm btn-light-primary p-1 px-2 me-1' title='Add a hand-set rate for this item'
											onClick={() => setEditing({ subjectId: row.subjectId, subjectName: row.subjectName, effectiveDate: row.effectiveDate, rate: row.rate })}>
											<i className='fas fa-plus'></i>
										</button>
										{row.anchor && (
											<button className='btn btn-sm btn-light-warning p-1 px-2 me-1' onClick={() => setEditing({ ...row })}><i className='fas fa-pen'></i></button>
										)}
										<button className='btn btn-sm btn-light-danger p-1 px-2' onClick={() => remove(row)}><i className='fas fa-trash'></i></button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>
			<div className='d-flex justify-content-between align-items-center mt-2'>
				<span className='text-muted small'>{data.total} row(s)</span>
				<div>
					<button className='btn btn-sm btn-light me-1' disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
					<span className='small mx-2'>{page} / {lastPage}</span>
					<button className='btn btn-sm btn-light' disabled={page >= lastPage} onClick={() => setPage(page + 1)}>Next</button>
				</div>
			</div>

			<Modal show={!!editing} onHide={() => setEditing(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{editing?.id ? 'Edit hand-set rate' : 'Add hand-set rate'}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className='fw-bold mb-3'>{editing?.subjectName}</p>
					<Form.Group className='mb-3'>
						<Form.Label>Effective date</Form.Label>
						<Form.Control type='date' value={editing?.effectiveDate || ''} onChange={(e) => setEditing({ ...editing, effectiveDate: e.target.value })} />
					</Form.Group>
					<Form.Group className='mb-3'>
						<Form.Label>Rate</Form.Label>
						<Form.Control type='number' step='any' min='0' value={editing?.rate ?? ''} onChange={(e) => setEditing({ ...editing, rate: e.target.value })} />
					</Form.Group>
					<Form.Group className='mb-3'>
						<Form.Label>Note</Form.Label>
						<Form.Control maxLength={50} value={editing?.note || ''} onChange={(e) => setEditing({ ...editing, note: e.target.value })} />
					</Form.Group>
					<Form.Check type='checkbox' id='edit-cascade' label='Also re-cost documents and vouchers from this date' checked={cascade} onChange={(e) => setCascade(e.target.checked)} />
				</Modal.Body>
				<Modal.Footer>
					<button className='btn btn-sm btn-secondary' onClick={() => setEditing(null)}>Cancel</button>
					<button className='btn btn-sm btn-primary' disabled={!editing?.effectiveDate || editing?.rate === '' || editing?.rate === undefined} onClick={save}>Save and rebuild</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

interface ValuationLine {
	id: number
	name: string
	code: string | null
	unit: string | null
	quantity: number
	rate: number | null
	/** 'po' when the item had no average rate and its latest PO rate was used */
	rateSource?: 'average' | 'po' | null
	value: number
}

interface ValuationGroup {
	key: string
	title: string
	parent: string | null
	ledger: { id: number; name: string } | null
	inventoryValue: number
	glValue: number | null
	difference: number | null
	noRateCount: number
	poRateCount?: number
	negativeCount: number
	lines: ValuationLine[]
}

interface ValuationSection {
	key: string
	title: string
	inventoryValue: number
	glValue: number
	difference: number
	groups: ValuationGroup[]
}

/** a difference under one unit of currency is rounding */
const differenceClass = (value: number | null) => (value === null ? 'text-muted' : Math.abs(value) < 1 ? 'text-success' : 'text-danger')

const ValuationTab: React.FC<{ scope: any }> = ({ scope }) => {
	const [data, setData] = useState<any>(null)
	const [loading, setLoading] = useState(false)
	const [open, setOpen] = useState<Record<string, boolean>>({})

	const load = () => {
		setLoading(true)
		post('valuation', { idProjects: scope.idProjects, toDate: scope.toDate })
			.then((resp) => (resp.success ? setData(resp.data) : toast.error(errorText(resp), { position: 'bottom-right' })))
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		setData(null)
		setOpen({})
		load()
	}, [scope.idProjects, scope.toDate])

	const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }))

	const valueCells = (row: { inventoryValue: number; glValue: number | null; difference: number | null }) => (
		<>
			<td className='text-end'>{amount(row.inventoryValue)}</td>
			<td className='text-end'>{amount(row.glValue)}</td>
			<td className={'text-end fw-bold ' + differenceClass(row.difference)}>{amount(row.difference)}</td>
		</>
	)

	const groupRows = (group: ValuationGroup) => (
		<React.Fragment key={group.key}>
			<tr style={{ cursor: group.lines.length ? 'pointer' : 'default' }} onClick={() => group.lines.length && toggle(group.key)}>
				<td className='ps-4'>
					{group.lines.length > 0 && <i className={'fas fa-chevron-' + (open[group.key] ? 'down' : 'right') + ' me-2 small text-muted'}></i>}
					{group.title}
					{group.noRateCount > 0 && <Badge bg='warning' text='dark' className='ms-2' title='Items in stock without an average rate on this date (valued at 0)'>{group.noRateCount} no rate</Badge>}
					{(group.poRateCount ?? 0) > 0 && <Badge bg='info' className='ms-1' title='Items without an average rate on this date, valued at their latest PO rate'>{group.poRateCount} PO rate</Badge>}
					{group.negativeCount > 0 && <Badge bg='danger' className='ms-1' title='Lines with negative stock'>{group.negativeCount} negative</Badge>}
				</td>
				<td className='small'>{group.ledger ? `${group.ledger.name} (#${group.ledger.id})` : <span className='text-danger'>No GL ledger</span>}</td>
				{valueCells(group)}
				<td className='text-end'>{group.lines.length}</td>
			</tr>
			{open[group.key] && (
				<tr>
					<td colSpan={6} className='p-0'>
						<div style={{ maxHeight: '45vh', overflow: 'auto' }}>
							<Table size='sm' bordered className='mb-0 small'>
								<thead>
									<tr>
										<th style={stickyHeaderStyle}>Name</th>
										<th style={stickyHeaderStyle}>Code</th>
										<th style={stickyHeaderStyle} className='text-end'>Quantity</th>
										<th style={stickyHeaderStyle}>Unit</th>
										<th style={stickyHeaderStyle} className='text-end'>Rate</th>
										<th style={stickyHeaderStyle} className='text-end'>Value</th>
									</tr>
								</thead>
								<tbody>
									{group.lines.map((line) => (
										<tr key={line.id} className={line.quantity < 0 ? 'table-danger' : line.rate === null ? 'table-warning' : ''}>
											<td>{line.name}</td>
											<td>{line.code || '-'}</td>
											<td className='text-end'>{amount(line.quantity)}</td>
											<td>{line.unit || '-'}</td>
											<td className='text-end'>
												{line.rateSource === 'po' && <Badge bg='info' className='me-1' title='No average rate on this date: latest PO rate'>PO</Badge>}
												{line.rate === null ? 'no rate' : amount(line.rate)}
											</td>
											<td className='text-end'>{amount(line.value)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</td>
				</tr>
			)}
		</React.Fragment>
	)

	/** raw material categories under a subtotal row per parent category (Raw Materials, Packing Materials, ...) */
	const sectionBody = (section: ValuationSection) => {
		if (section.key !== 'raw') {
			return section.groups.map(groupRows)
		}
		const parents: { name: string; groups: ValuationGroup[] }[] = []
		section.groups.forEach((group) => {
			const name = group.parent || 'Other'
			const last = parents[parents.length - 1]
			if (last && last.name === name) {
				last.groups.push(group)
			} else {
				parents.push({ name, groups: [group] })
			}
		})

		return parents.map((parent) => {
			const sum = (key: 'inventoryValue' | 'glValue') => parent.groups.reduce((total, group) => total + Number(group[key] || 0), 0)
			const inventoryValue = sum('inventoryValue')
			const glValue = sum('glValue')
			return (
				<React.Fragment key={'parent-' + parent.name}>
					<tr className='table-light'>
						<td className='ps-3 fw-semibold'>{parent.name}</td>
						<td></td>
						{valueCells({ inventoryValue, glValue, difference: inventoryValue - glValue })}
						<td></td>
					</tr>
					{parent.groups.map(groupRows)}
				</React.Fragment>
			)
		})
	}

	return (
		<>
			<div className='d-flex justify-content-between align-items-center mb-2'>
				<div className='text-muted small'>
					{data
						? <>As of <strong>{data.asOfDate}</strong> (the To Date, or today). Stock from {data.inventoryFrom || 'the start'} valued at the rate on that date
							{data.averageProject ? ' (finished goods at their average rate)' : ' (finished goods at their fixed production cost)'};
							GL = opening balance + vouchers from {data.accountingFrom || 'the start'}. Click a row for its items. Read only.</>
						: 'Inventory value against the GL ledger balance.'}
				</div>
				<button className='btn btn-sm btn-light-primary' onClick={load} disabled={loading}>
					{loading ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='fas fa-sync-alt me-1'></i>}Refresh
				</button>
			</div>
			<div style={{ maxHeight: '65vh', overflow: 'auto' }}>
				<Table bordered hover size='sm' className='mb-0'>
					<thead>
						<tr>
							<th style={stickyHeaderStyle}>Group</th>
							<th style={stickyHeaderStyle}>GL ledger</th>
							<th style={stickyHeaderStyle} className='text-end'>Inventory value</th>
							<th style={stickyHeaderStyle} className='text-end'>GL value</th>
							<th style={stickyHeaderStyle} className='text-end'>Difference</th>
							<th style={stickyHeaderStyle} className='text-end'>Lines</th>
						</tr>
					</thead>
					<tbody>
						{!data ? (
							<tr><td colSpan={6} className='text-center py-3'>{loading ? <><span className='spinner-border spinner-border-sm me-2'></span>Loading…</> : 'No data.'}</td></tr>
						) : (
							(data.sections as ValuationSection[]).map((section) => (
								<React.Fragment key={section.key}>
									<tr className='table-secondary fw-bold'>
										<td colSpan={2}>{section.title}</td>
										{valueCells(section)}
										<td></td>
									</tr>
									{sectionBody(section)}
								</React.Fragment>
							))
						)}
					</tbody>
					{data && (
						<tfoot>
							<tr className='fw-bold'>
								<td colSpan={2} className='text-end'>Total</td>
								{valueCells(data.totals)}
								<td></td>
							</tr>
						</tfoot>
					)}
				</Table>
			</div>
		</>
	)
}

const RunHistoryTab: React.FC<{ idProjects?: number; migrations?: boolean }> = ({ idProjects, migrations }) => {
	const [rows, setRows] = useState<any[]>([])
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		setLoading(true)
		post('runs', { idProjects: idProjects || null, migrations: !!migrations, perPage: 100 })
			.then((resp) => setRows(resp.success ? resp.data.rows : []))
			.finally(() => setLoading(false))
	}, [idProjects, migrations])

	const summary = (run: any) => {
		if (run.error) return <span className='text-danger'>{run.error}</span>
		if (run.result?.checks) {
			return run.result.checks.filter((c: any) => c.before > 0).map((c: any) => `${c.key}: ${c.fixed} fixed, ${c.remaining} open`).join('; ') || 'nothing to fix'
		}
		return (run.result?.messages || []).join(' ')
	}

	return (
		<div style={{ maxHeight: '60vh', overflow: 'auto' }}>
			<Table bordered hover size='sm' className='mb-0'>
				<thead>
					<tr>
						<th style={stickyHeaderStyle}>When</th>
						<th style={stickyHeaderStyle}>By</th>
						<th style={stickyHeaderStyle}>Action</th>
						<th style={stickyHeaderStyle}>Check</th>
						<th style={stickyHeaderStyle}>Range</th>
						<th style={stickyHeaderStyle}>Mode</th>
						<th style={stickyHeaderStyle}>Result</th>
					</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr><td colSpan={7} className='text-center py-3'><span className='spinner-border spinner-border-sm me-2'></span>Loading…</td></tr>
					) : rows.length === 0 ? (
						<tr><td colSpan={7} className='text-center text-muted py-3'>No runs yet.</td></tr>
					) : (
						rows.map((run) => (
							<tr key={run.id}>
								<td className='text-nowrap small'>{run.created_at}</td>
								<td className='small'>{run.createdByName}</td>
								<td>{run.action}</td>
								<td>{run.check_key}{run.refs?.length ? <span className='text-muted small'> ({run.refs.length} selected)</span> : ''}</td>
								<td className='small text-nowrap'>{run.from_date || '…'} – {run.to_date || '…'}</td>
								<td>{Number(run.dry_run) ? <Badge bg='info'>preview</Badge> : <Badge bg={run.status === 'success' ? 'success' : 'danger'}>{run.status}</Badge>}</td>
								<td className='small'>{summary(run)} <span className='text-muted'>({run.seconds}s)</span></td>
							</tr>
						))
					)}
				</tbody>
			</Table>
		</div>
	)
}

const DataMigrationsTab: React.FC = () => {
	const [data, setData] = useState<any>({ rows: [], pending: 0, lastBatch: 0 })
	const [loading, setLoading] = useState(false)
	const [denied, setDenied] = useState<string | null>(null)
	const [filter, setFilter] = useState('pending')
	const [search, setSearch] = useState('')
	const [working, setWorking] = useState<string | null>(null)
	const [source, setSource] = useState<any>(null)
	const [outcome, setOutcome] = useState<any>(null)
	const [historyKey, setHistoryKey] = useState(0)

	const load = () => {
		setLoading(true)
		fetch(API + 'migrations', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				if (resp.success) {
					setData(resp.data)
					setDenied(null)
					if (!resp.data.pending && filter === 'pending') setFilter('recent')
				} else {
					setDenied(errorText(resp))
				}
			})
			.catch(() => toast.error('Could not load migrations', { position: 'bottom-right' }))
			.finally(() => setLoading(false))
	}
	useEffect(load, [])

	const rows = useMemo(() => {
		const term = search.trim().toLowerCase()
		return (data.rows || []).filter((row: any) => {
			if (filter === 'pending' && row.ran) return false
			if (filter === 'recent' && !(row.ran && Number(row.batch) === Number(data.lastBatch)) && row.ran) return false
			if (filter === 'data' && !row.changesData) return false
			return !term || (row.name + ' ' + row.description).toLowerCase().includes(term)
		})
	}, [data, filter, search])

	const LABELS: Record<string, [string, string, string]> = {
		run: ['Run migration?', 'Run', 'The migration is applied to the live database and recorded in the next batch.'],
		all: ['Run all pending migrations?', 'Run all', 'Every pending migration is applied in order and recorded in the next batch.'],
		preview: ['', '', ''],
		rerun: ['Run this data migration again?', 'Run again', 'Its up() runs again inside a transaction and is saved. Structure changes are refused.'],
		rollback: ['Roll back migration?', 'Roll back', 'Its down() runs and it is removed from the migrations table, so it shows as pending again.'],
	}

	const act = (action: 'run' | 'all' | 'preview' | 'rerun' | 'rollback', name: string | null) => {
		const [title, button, text] = LABELS[action]
		const confirm = action === 'preview'
			? Promise.resolve({ isConfirmed: true })
			: Swal.fire({
					title,
					html: `${name ? '<code>' + escapeHtml(name) + '</code><br/><br/>' : ''}${text}<br/><b>Take a database backup first.</b>`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: button,
					customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-secondary' },
					buttonsStyling: false,
			  })
		confirm.then((result: any) => {
			if (!result.isConfirmed) return
			setWorking(name || 'all')
			post('migrations/run', { action: action === 'all' ? 'run' : action, name })
				.then((resp) => {
					setOutcome({ ...resp, action, name: name || 'All pending migrations' })
					if (resp.success && action !== 'preview') load()
					setHistoryKey((key) => key + 1)
				})
				.catch(() => toast.error('Request failed', { position: 'bottom-right' }))
				.finally(() => setWorking(null))
		})
	}

	const view = (name: string) =>
		post('migrations/source', { name }).then((resp) =>
			resp.success ? setSource({ name, code: resp.result?.source }) : Swal.fire({ icon: 'error', title: 'Not found', html: errorText(resp) })
		)

	if (denied) {
		return <div className='alert alert-warning mb-0'>{denied}</div>
	}

	return (
		<>
			<p className='text-muted small'>
				Database migrations of this system. <b>Run</b> applies a pending migration (like <code>php artisan migrate</code>). <b>Preview</b> runs a data
				migration inside a transaction and rolls it back, showing the rows it would change. <b>Run again</b> repeats a data migration (for example a
				rate backfill). <b>Roll back</b> undoes a migration of the latest batch. Only Super Admins can use this tab.
			</p>
			<div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
				<Form.Select size='sm' style={{ maxWidth: 210 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
					<option value='pending'>Pending ({data.pending})</option>
					<option value='recent'>Pending + latest batch ({data.lastBatch})</option>
					<option value='data'>Data migrations</option>
					<option value='all'>All ({(data.rows || []).length})</option>
				</Form.Select>
				<Form.Control size='sm' style={{ maxWidth: 260 }} placeholder='Search migration…' value={search} onChange={(e) => setSearch(e.target.value)} />
				<button className='btn btn-sm btn-light' disabled={loading} onClick={load}><i className='fas fa-sync me-1'></i>Refresh</button>
				<button className='btn btn-sm btn-danger ms-auto' disabled={!data.pending || !!working} onClick={() => act('all', null)}>
					{working === 'all' ? <span className='spinner-border spinner-border-sm me-2'></span> : <i className='fas fa-play me-1'></i>}
					Run all pending ({data.pending})
				</button>
			</div>
			<div style={{ maxHeight: '55vh', overflow: 'auto' }} className='mb-4'>
				<Table bordered hover size='sm' className='mb-0'>
					<thead>
						<tr>
							<th style={stickyHeaderStyle}>Migration</th>
							<th style={stickyHeaderStyle}>Changes</th>
							<th style={stickyHeaderStyle}>Status</th>
							<th style={stickyHeaderStyle}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr><td colSpan={4} className='text-center py-3'><span className='spinner-border spinner-border-sm me-2'></span>Loading…</td></tr>
						) : rows.length === 0 ? (
							<tr><td colSpan={4} className='text-center text-muted py-3'>{filter === 'pending' ? 'No pending migrations. The database is up to date.' : 'No migrations.'}</td></tr>
						) : (
							rows.map((row: any) => (
								<tr key={row.name}>
									<td>
										<div className='fw-semibold text-break'>{row.name}</div>
										{row.description && <div className='text-muted small'>{row.description}</div>}
									</td>
									<td className='text-nowrap'>
										{row.changesStructure && <Badge bg='info' className='me-1'>structure</Badge>}
										{row.changesData && <Badge bg='primary'>data</Badge>}
									</td>
									<td className='text-nowrap'>
										{row.missingFile ? <Badge bg='secondary'>file missing</Badge> : row.ran ? <Badge bg='success'>ran (batch {row.batch})</Badge> : <Badge bg='warning' text='dark'>pending</Badge>}
									</td>
									<td className='text-nowrap'>
										{working === row.name ? (
											<span className='spinner-border spinner-border-sm'></span>
										) : !row.missingFile && (
											<>
												<button className='btn btn-sm btn-light p-1 px-2 me-1' onClick={() => view(row.name)}>View</button>
												{row.changesData && (
													<button className='btn btn-sm btn-light-primary p-1 px-2 me-1' disabled={!!working} onClick={() => act('preview', row.name)}>Preview</button>
												)}
												{!row.ran && (
													<button className='btn btn-sm btn-danger p-1 px-2 me-1' disabled={!!working} onClick={() => act('run', row.name)}>Run</button>
												)}
												{row.ran && row.changesData && (
													<button className='btn btn-sm btn-light-danger p-1 px-2 me-1' disabled={!!working} onClick={() => act('rerun', row.name)}>Run again</button>
												)}
												{row.canRollback && (
													<button className='btn btn-sm btn-light-warning p-1 px-2' disabled={!!working} onClick={() => act('rollback', row.name)}>Roll back</button>
												)}
											</>
										)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>

			<h6 className='mb-2'>Migration history</h6>
			<RunHistoryTab key={historyKey} migrations />

			<Modal show={!!source} onHide={() => setSource(null)} size='xl'>
				<Modal.Header closeButton>
					<Modal.Title className='text-break'>{source?.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<pre className='bg-light p-3 small mb-0' style={{ maxHeight: '65vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{source?.code}</pre>
				</Modal.Body>
			</Modal>

			<Modal show={!!outcome} onHide={() => setOutcome(null)} size='lg'>
				<Modal.Header closeButton>
					<Modal.Title className='text-break'>{outcome?.action === 'preview' ? 'Preview' : 'Result'}: {outcome?.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{outcome && !outcome.success && <div className='alert alert-danger'>{outcome.errorMessage || outcome.error}</div>}
					{outcome?.success && (
						<>
							<p className={outcome.dryRun ? 'text-primary' : 'text-success'}>{outcome.dryRun ? 'Nothing was saved.' : 'Done.'} ({outcome.seconds}s)</p>
							<pre className='bg-light p-3 small mb-0' style={{ whiteSpace: 'pre-wrap' }}>{(outcome.result?.messages || []).join('\n')}</pre>
						</>
					)}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default InventoryDataIntegrity
